/**
 * Shared helpers for format-level removal campaign display logic
 * Provides unified UI display logic for both operators and admins
 */

export interface FormatRemovalInstruction {
  formatId: number;
  formatName: string;
  expectedCount: number;
  displayText: string;
}

export interface RemovalDisplayData {
  isFormatLevel: boolean;
  displayText: string;
  formats?: FormatRemovalInstruction[];
  packName?: string;
}

/**
 * Generates format-level removal instruction text from campaign tasks
 * Example: "Remove 2x SV Floor Vinyls, 1x Freezer Hanger"
 */
export function generateFormatRemovalText(formats: FormatRemovalInstruction[]): string {
  if (formats.length === 0) {
    return 'No removal required';
  }

  if (formats.length === 1) {
    const format = formats[0];
    return `Remove ${format.expectedCount}x ${format.formatName}`;
  }

  const formatParts = formats.map(format => 
    `${format.expectedCount}x ${format.formatName}`
  );

  if (formatParts.length === 2) {
    return `Remove ${formatParts[0]} and ${formatParts[1]}`;
  }

  const lastFormat = formatParts.pop();
  return `Remove ${formatParts.join(', ')}, and ${lastFormat}`;
}

/**
 * Determines removal display data based on campaign configuration
 * Routes between format-level and pack-based display logic
 */
export function getRemovalDisplayData(
  campaign: { enableFormatLevelRemoval?: boolean },
  task: {
    packName?: string;
    formatId?: number;
    expectedCount?: number;
    mediaRequirements?: Array<{
      formatId: number;
      formatName: string;
      quantity: number;
    }>;
  }
): RemovalDisplayData {
  // Use format-level logic if campaign has the feature flag enabled
  if (campaign.enableFormatLevelRemoval) {
    // For format-level removal campaigns, extract format info from task
    const formats: FormatRemovalInstruction[] = [];

    if (task.formatId && task.expectedCount) {
      // Single format task
      formats.push({
        formatId: task.formatId,
        formatName: getFormatNameById(task.formatId),
        expectedCount: task.expectedCount,
        displayText: `${task.expectedCount}x ${getFormatNameById(task.formatId)}`
      });
    } else if (task.mediaRequirements) {
      // Multiple formats from media requirements
      task.mediaRequirements.forEach(req => {
        formats.push({
          formatId: req.formatId,
          formatName: req.formatName,
          expectedCount: req.quantity,
          displayText: `${req.quantity}x ${req.formatName}`
        });
      });
    }

    return {
      isFormatLevel: true,
      displayText: generateFormatRemovalText(formats),
      formats
    };
  } else {
    // Fall back to legacy pack-based display
    return {
      isFormatLevel: false,
      displayText: `Remove ${task.packName || 'Pack'}`,
      packName: task.packName
    };
  }
}

/**
 * Helper to get format name by ID (placeholder - should be replaced with actual format lookup)
 */
function getFormatNameById(formatId: number): string {
  // This is a placeholder - in real implementation, this would look up from a formats cache/context
  // For now, return a generic name that includes the ID for debugging
  const formatMap: Record<number, string> = {
    1: 'SV Shelf Talker',
    2: 'SV Floor Graphic', 
    3: 'SV Beer Vinyl',
    4: 'SV Aisle Fin',
    5: 'Freezer Hanger',
    6: 'Window Cling',
    7: 'Door Graphic'
  };
  
  return formatMap[formatId] || `Format ${formatId}`;
}

/**
 * Checks if a campaign is using format-level removals
 */
export function isFormatLevelRemovalCampaign(campaign: { enableFormatLevelRemoval?: boolean }): boolean {
  return Boolean(campaign.enableFormatLevelRemoval);
}

/**
 * Generates admin sign-off summary for format-level removals
 */
export function generateAdminRemovalSummary(
  campaignName: string,
  storeName: string,
  formats: FormatRemovalInstruction[]
): {
  title: string;
  subtitle: string;
  formatList: string[];
} {
  return {
    title: `Format Removal - ${campaignName}`,
    subtitle: `Store: ${storeName}`,
    formatList: formats.map(format => 
      `${format.formatName}: ${format.expectedCount} unit${format.expectedCount === 1 ? '' : 's'}`
    )
  };
}

/**
 * Validates format removal completion data
 */
export function validateFormatRemovalCompletion(
  formats: FormatRemovalInstruction[],
  completedFormats: Array<{ formatId: number; actualCount: number; reason?: string }>
): {
  isValid: boolean;
  errors: string[];
  completionSummary: string;
} {
  const errors: string[] = [];
  let totalExpected = 0;
  let totalCompleted = 0;

  formats.forEach(format => {
    totalExpected += format.expectedCount;
    const completion = completedFormats.find(c => c.formatId === format.formatId);
    
    if (!completion) {
      errors.push(`Missing completion data for ${format.formatName}`);
    } else {
      totalCompleted += completion.actualCount;
      
      if (completion.actualCount < format.expectedCount && !completion.reason) {
        errors.push(`Discrepancy reason required for ${format.formatName} (expected ${format.expectedCount}, actual ${completion.actualCount})`);
      }
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
    completionSummary: `${totalCompleted}/${totalExpected} formats removed`
  };
}